import React, { useState, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import './MobileAtlasHeaderMolecule.scss';
import { useTranslation } from 'localization';
import { FilterExpression, PerspectiveFilter } from 'types/filters';
import { SortOrder, SortDirection } from 'types/context';
import MultiSelectComponent from '../../../atoms/multi-select/multi-select';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useAvailableTypes } from '../../../../hooks/useAvailableTypes';

interface MobileAtlasHeaderMolecule {
  filterSortState?: any;
  featuredSphereId?: string;
  resetFilterSortState?: () => void;
  baseSchema?: any;
  addOrUpdatePropertyFilter?: (filter: FilterExpression) => void;
  addOrUpdatePerspectiveFilter?: (filter: PerspectiveFilter) => void;
  removePropertyFilterByKey?: (property: string) => void;
  addOrUpdateSortOrder?: (sort: SortOrder) => void;
  removeSortOrderByKey?: (property: string) => void;
}

const MobileAtlasHeaderMolecule: React.FC<MobileAtlasHeaderMolecule> = ({
  filterSortState,
  featuredSphereId,
  resetFilterSortState,
  baseSchema,
  addOrUpdatePropertyFilter,
  addOrUpdatePerspectiveFilter,
  removePropertyFilterByKey,
  addOrUpdateSortOrder,
  removeSortOrderByKey,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { schemas: availableSphereTypeSchemas, loading: availableTypesLoading } =
    useAvailableTypes(apiClients, featuredSphereId);

  const [filterSidebarVisible, setFilterSidebarVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<string | null>(null);

  type UrgencyOption = {
    name: string;
    code: string;
    avatar: string;
  };

  type SphereTypeOption = {
    name: string;
    avatar: string;
    type: string;
  };

  const [urgencySelectedOptions, setUrgencySelectedOptions] = useState<UrgencyOption[]>([]);
  const [sphereTypeOptions, setSphereTypeOptions] = useState<SphereTypeOption[]>([]);
  const [sphereSelectedOptions, setSphereSelectedOptions] = useState<SphereTypeOption[]>([]);
  const [loadingSphereTypes, setLoadingSphereTypes] = useState(false);

  const urgencyOptions: UrgencyOption[] = [
    { name: t('atlas.urgency.urgent'), code: '5', avatar: 'urgent-icon' },
    { name: t('atlas.urgency.high'), code: '4', avatar: 'high-icon' },
    { name: t('atlas.urgency.medium'), code: '3', avatar: 'medium-icon' },
    { name: t('atlas.urgency.low'), code: '2', avatar: 'low-icon' },
    { name: t('atlas.urgency.parked'), code: '1', avatar: 'parked-icon' },
  ];

  const sortOptions = [
    { label: t('atlas.sort.nameAsc'), value: 'name:asc' },
    { label: t('atlas.sort.nameDesc'), value: 'name:desc' },
  ];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (addOrUpdatePropertyFilter) {
      if (query.trim()) {
        addOrUpdatePropertyFilter({
          property: 'name',
          operator: 'contains',
          values: [query]
        });
      } else {
        removePropertyFilterByKey?.('name');
      }
    }
  };

  const handleSortChange = (e: { value: string }) => {
    setSortOption(e.value);
    const [field, order] = e.value.split(':');

    if (addOrUpdateSortOrder) {
      addOrUpdateSortOrder({
        property: field,
        orderBy: order as SortDirection
      });
    }
  };

  const handleUrgencySelection = (selectedOptions: any[] | null) => {
    const urgencyOptions = (selectedOptions || []) as UrgencyOption[];
    setUrgencySelectedOptions(urgencyOptions);

    const urgencyCodes = urgencyOptions.map((o) => o.code).filter(Boolean);
    if (urgencyCodes.length > 0) {
      addOrUpdatePropertyFilter?.({
        property: 'urgency',
        operator: 'in',
        values: urgencyCodes,
      });
    } else {
      removePropertyFilterByKey?.('urgency');
    }
  };

  const handleSphereTypeSelection = (selectedOptions: any[] | null) => {
    const sphereOptions = (selectedOptions || []) as SphereTypeOption[];
    setSphereSelectedOptions(sphereOptions);

    const sphereTypes = sphereOptions.map((o) => o.type).filter(Boolean);
    if (sphereTypes.length > 0) {
      addOrUpdatePropertyFilter?.({
        property: 'type',
        operator: 'in',
        values: sphereTypes,
      });
    } else {
      removePropertyFilterByKey?.('type');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSortOption(null);
    setUrgencySelectedOptions([]);
    setSphereSelectedOptions([]);
    resetFilterSortState?.();
  };

  // Populate sphere-type filter options from the available-types hook.
  useEffect(() => {
    if (!filterSidebarVisible) return;
    setLoadingSphereTypes(availableTypesLoading);
    if (!availableTypesLoading) {
      const formatted = availableSphereTypeSchemas
        .filter((s: any) => !s.is_abstract)
        .map((s: any) => ({
          name: s.name,
          avatar: s.avatar || 'default-icon',
          type: s.system_name,
        }));
      formatted.sort((a: any, b: any) => {
        const nameA = a.name || a.type || '';
        const nameB = b.name || b.type || '';
        return nameA.localeCompare(nameB);
      });
      setSphereTypeOptions(formatted);
    }
  }, [filterSidebarVisible, availableSphereTypeSchemas, availableTypesLoading]);

  // Calculate the count of active filters
  const activeFilterCount = [
    searchQuery.trim() !== '', // Search filter
    sortOption !== null, // Sort filter
    urgencySelectedOptions.length > 0, // Urgency filter
    sphereSelectedOptions.length > 0, // Sphere type filter
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className="mobile-atlas-header">
      <div className="mobile-atlas-header__top">
        <div className="mobile-atlas-header__search">
          <span className="p-input-icon-left p-input-icon-right w-full">
            <i className="pi pi-search" />
            <InputText
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('atlas.search.placeholder')}
              className="w-full Search-input-box"
            />
            {searchQuery && (
              <button
                type="button"
                className="mobile-atlas-header__clear-button"
                onClick={() => {
                  setSearchQuery('');
                  removePropertyFilterByKey?.('name');
                }}
                aria-label={t('atlas.search.clear')}
              >
                <i className="pi pi-times" />
              </button>
            )}
          </span>
        </div>

        <Button
          icon="pi pi-filter"
          className="mobile-atlas-header__filter-button"
          onClick={() => setFilterSidebarVisible(true)}
          badge={hasActiveFilters ? String(activeFilterCount) : undefined}
          badgeClassName="p-badge-danger"
          rounded
          outlined
        />
      </div>

      <Sidebar
        visible={filterSidebarVisible}
        position="right"
        onHide={() => setFilterSidebarVisible(false)}
        className="mobile-filter-sidebar"
        header={t('atlas.filters.title')}
      >
        <div className="mobile-filter-content">
          <div className="filter-section">
            <label className="filter-label">
              {t('atlas.urgency.label')}
            </label>
            <MultiSelectComponent
              options={urgencyOptions}
              value={urgencySelectedOptions}
              placeholder={t('atlas.urgency.placeholder')}
              maxSelectedLabels={3}
              onSelectionChange={handleUrgencySelection}
              className="w-full urgency-multiselect"
              panelClassName='urgency-type-panel'
              filter={false}
            />
          </div>

          <div className="filter-section">
            <label className="filter-label">
              {t('atlas.sphereType.label')}
            </label>
            <MultiSelectComponent
              options={sphereTypeOptions}
              value={sphereSelectedOptions}
              placeholder={
                loadingSphereTypes
                  ? t('atlas.sphereType.loading')
                  : t('atlas.sphereType.placeholder')
              }
              maxSelectedLabels={5}
              onSelectionChange={handleSphereTypeSelection}
              className="w-full sphere-type-multiselect"
              panelClassName="sphere-type-panel"
              filter={true}
            />
          </div>

          <div className="filter-section">
            <label className="filter-label">
              {t('atlas.sort.label')}
            </label>
            <Dropdown
              value={sortOption}
              options={sortOptions}
              onChange={handleSortChange}
              placeholder={t('atlas.sort.placeholder')}
              className="w-full"
              panelClassName="sort-by-panel"
            />
          </div>

          <div className="filter-section">
            <label className="filter-label">
              {t('atlas.search.label')}
            </label>
            <InputText
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('atlas.search.placeholder')}
              className="w-full Search-input-box"
            />
          </div>

          {hasActiveFilters && (
            <div className="filter-actions">
              <Button
                label={t('atlas.filters.clear')}
                icon="pi pi-times"
                onClick={handleClearFilters}
                className="p-button-outlined p-button-danger w-full"
              />
            </div>
          )}
        </div>
      </Sidebar>
    </div>
  );
};

export default MobileAtlasHeaderMolecule;
